import { FC, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTrails } from '@/hooks/useTrails';
import LoadingSpinner from '@/components/LoadingSpinner';
import TrailsTour from '@/components/tour/TrailsTour';


const TrailsPage: FC = () => {
  const {
    data,
    tags,
    isLoading,
    error,
    filters,
    filteredTrails,
    updateFilters,
  } = useTrails();

  const [searchInput, setSearchInput] = useState('');

  const handleSearch = (value: string) => {
    setSearchInput(value);
    updateFilters({ search: value });
  };

  const handleTagFilter = (tagSlug: string) => {
    updateFilters({ tag: tagSlug });
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'Iniciante':
        return 'text-accent-cyan';
      case 'Intermediário':
        return 'text-yellow-400';
      case 'Avançado':
        return 'text-accent-red';
      default:
        return 'text-content-muted';
    }
  };

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-accent-red text-lg font-bold">Erro ao carregar trilhas</p>
          <p className="text-content-muted mt-2">{error}</p>
        </div>
      </div>
    );
  }

  // Banner didático: usuário ainda não começou nenhuma trilha
  const userHasNoStartedTrails =
    filteredTrails.length > 0 &&
    filteredTrails.every((t) => t.status !== 'active' && t.status !== 'completed');
  const firstTrail =
    filteredTrails.find((t) => t.title.startsWith('Trilha 1')) ?? filteredTrails[0];

  return (
      <div className="bg-surface-primary min-h-screen">
        {/* Main Content */}
        <main className="flex flex-1 justify-center py-8">
        <div className="max-w-[1280px] w-full px-4 md:px-10 flex flex-col gap-10">
          {/* Header Section */}
          <div className="flex flex-col gap-6" data-tour="trails-search">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
              <div className="flex flex-col gap-1">
                <h1 className="text-3xl md:text-4xl font-bold text-content-primary">
                  Explorar Trilhas
                </h1>
                <p className="text-content-secondary">
                  Escolha o seu próximo passo na jornada de aprendizado.
                </p>
              </div>

              {/* Search */}
              <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                <div className="relative flex-1 sm:w-80">
                  <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-content-muted text-xl">
                    search
                  </span>
                  <input
                    type="text"
                    placeholder="Buscar trilhas..."
                    value={searchInput}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="w-full bg-surface-tertiary border border-theme-border rounded-lg pl-10 py-2.5 text-sm text-content-primary placeholder-content-muted focus:ring-accent-cyan focus:border-accent-cyan transition-all"
                  />
                </div>
              </div>
            </div>

            {/* Tag Filters — dynamic from API */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
              <button
                onClick={() => handleTagFilter('all')}
                className={`px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${
                  filters.tag === 'all'
                    ? 'bg-accent text-accent-contrast'
                    : 'bg-surface-tertiary text-content-secondary hover:bg-surface-hover'
                }`}
              >
                Todas as Trilhas
              </button>
              {tags.map((tag) => (
                <button
                  key={tag.id}
                  onClick={() => handleTagFilter(tag.slug)}
                  className={`px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${
                    filters.tag === tag.slug
                      ? 'bg-accent text-accent-contrast'
                      : 'bg-surface-tertiary text-content-secondary hover:bg-surface-hover'
                  }`}
                >
                  {tag.name}
                </button>
              ))}
            </div>
          </div>

          {/* Banner — usuário sem trilhas iniciadas */}
          {userHasNoStartedTrails && firstTrail && (
            <div className="-mt-4 p-4 rounded-xl bg-accent/10 border border-accent/20 flex items-start gap-3">
              <span className="material-symbols-outlined text-accent text-2xl shrink-0">tips_and_updates</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-content-primary mb-1">Não sabe por onde começar?</p>
                <p className="text-xs text-content-secondary mb-2">
                  A <b>{firstTrail.title}</b> é o ponto de partida recomendado pra quem está começando.
                </p>
                <Link
                  to={`/trilhas/${firstTrail.id}`}
                  className="inline-flex items-center gap-1 text-xs font-bold text-accent hover:text-accent-hover"
                >
                  Começar agora <span className="material-symbols-outlined text-sm">arrow_forward</span>
                </Link>
              </div>
            </div>
          )}

          {/* Trails Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredTrails.map((trail, index) => (
              <div
                key={trail.id}
                data-tour={index === 0 ? 'trails-card' : undefined}
                className={`trail-card group relative flex flex-col rounded-2xl overflow-hidden shadow-xl transition-all duration-300 hover:-translate-y-1 ${
                  trail.status === 'active'
                    ? 'bg-surface-card border-2 border-accent-cyan shadow-accent-cyan/10'
                    : 'bg-surface-card/40 border border-theme-border grayscale-[0.5]'
                }`}
              >
                {/* Trail Image */}
                <div className="relative h-48 w-full overflow-hidden">
                  <div
                    className={`absolute inset-0 bg-cover bg-center ${
                      trail.status === 'active'
                        ? 'transition-transform duration-500 group-hover:scale-110'
                        : ''
                    }`}
                    style={{ backgroundImage: `url("${trail.imageUrl}")` }}
                  ></div>
                  <div
                    className={`absolute inset-0 ${
                      trail.status === 'active'
                        ? 'bg-gradient-to-t from-black/60 to-transparent'
                        : 'bg-black/40'
                    }`}
                  ></div>

                  {/* Badge */}
                  {trail.badge && (
                    <div
                      className={`absolute top-4 ${
                        trail.status === 'active' ? 'left-4' : 'right-4'
                      } px-3 py-1 rounded-full text-2xs font-black uppercase tracking-wider ${
                        trail.status === 'active'
                          ? 'bg-accent-cyan text-[#001420]'
                          : 'bg-black/60 text-white border border-white/20 backdrop-blur-md'
                      }`}
                    >
                      {trail.badge}
                    </div>
                  )}
                </div>

                {/* Trail Content */}
                <div className="flex flex-col p-6 gap-4 flex-1">
                  {trail.sector && (
                    <span className="inline-flex items-center gap-1 text-2xs font-bold uppercase tracking-wider text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded-full w-fit">
                      <span className="material-symbols-outlined text-xs">domain</span>
                      {trail.sector.name}
                    </span>
                  )}
                  <div className="flex flex-col gap-1">
                    <h3
                      className={`text-xl font-bold text-content-primary ${
                        trail.status === 'active'
                          ? 'group-hover:text-accent-cyan'
                          : ''
                      } transition-colors`}
                    >
                      {trail.title}
                    </h3>
                    <p className="text-sm text-content-secondary line-clamp-2">
                      {trail.description}
                    </p>
                  </div>

                  {/* Trail Meta */}
                  <div className="flex items-center gap-4 text-2xs font-bold uppercase tracking-wider text-content-muted">
                    <div className="flex items-center gap-1">
                      <span className="material-symbols-outlined text-sm">schedule</span>
                      {trail.duration}
                    </div>
                    <div className={`flex items-center gap-1 ${getLevelColor(trail.level)}`}>
                      <span className="material-symbols-outlined text-sm">
                        signal_cellular_alt
                      </span>
                      {trail.level}
                    </div>
                  </div>

                  {/* Progress (for active trails) */}
                  {trail.status === 'active' && trail.progress !== undefined && (
                    <div className="flex flex-col gap-2 mt-auto">
                      <div className="flex justify-between items-center text-xs font-bold">
                        <span className="text-content-muted">Progresso</span>
                        <span className="text-accent-cyan">{trail.progress}%</span>
                      </div>
                      <div className="h-1.5 w-full bg-surface-tertiary rounded-full overflow-hidden">
                        <div
                          className="h-full bg-accent-cyan"
                          style={{ width: `${trail.progress}%` }}
                        ></div>
                      </div>
                    </div>
                  )}

                  {/* Action Button */}
                  <Link
                    to={trail.status === 'active' ? `/trilhas/${trail.id}` : '#'}
                    onClick={(e) => {
                      if (trail.status === 'locked') {
                        e.preventDefault();
                      }
                    }}
                    className={`mt-4 w-full py-3 font-bold rounded-lg transition-all flex items-center justify-center gap-2 ${
                      trail.status === 'active'
                        ? 'bg-accent hover:bg-accent-hover text-accent-contrast'
                        : 'bg-surface-tertiary text-content-muted cursor-not-allowed pointer-events-none'
                    }`}
                  >
                    {trail.status === 'active' ? (
                      <>
                        Continuar
                        <span className="material-symbols-outlined text-lg">play_arrow</span>
                      </>
                    ) : (
                      'Bloqueado'
                    )}
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* Recommendation Section */}
          {data?.recommendedTrail && (
            <section className="mt-12 p-8 rounded-2xl bg-gradient-to-r from-accent/10 to-accent-cyan/10 border border-accent/20 flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="flex flex-col gap-4 max-w-xl">
                <div className="flex items-center gap-2 text-accent">
                  <span className="material-symbols-outlined">lightbulb</span>
                  <span className="text-xs font-bold uppercase tracking-widest">
                    Dica da Semana
                  </span>
                </div>
                <h2 className="text-2xl font-bold text-content-primary">
                  Personalize sua trilha de aprendizado
                </h2>
                <p className="text-content-secondary">
                  Nossa IA pode sugerir as melhores missões baseadas no seu perfil e objetivos
                  de carreira. Responda ao quiz rápido de diagnóstico.
                </p>
                <button className="w-fit px-6 py-2.5 bg-accent text-accent-contrast font-bold rounded-lg hover:scale-105 transition-transform">
                  Fazer Diagnóstico
                </button>
              </div>
              <div className="flex -space-x-4">
                <div
                  className="size-16 rounded-full border-4 border-surface-primary bg-cover bg-center"
                  style={{
                    backgroundImage:
                      'url("https://lh3.googleusercontent.com/aida-public/AB6AXuBupiy58233rOQ3t-PTFl68e_iggVSuh2JFibrD5qQA-ta8wwkTfUNItA25ZPnBoswsrdDaR0AKmUm8viVgTm8V4UzrqouJEjxD5KsTgskwqrITazHeEPlCPHkUWnDw45lqKpdLNSYENJRNTj_U7gvIB2NdYOWBOWVs_5KEjSOuJXaIMXM6ioV2URq1I2Jf6ARnc9ws6AUGjpMWenfa605jYmJzr0cMTuHeMXVd-IHqTFx9EDunDOp_Sd5cpVm6g4WzgK3oUeboIrk")',
                  }}
                ></div>
                <div className="size-16 rounded-full border-4 border-surface-primary bg-surface-tertiary"></div>
                <div className="size-16 rounded-full border-4 border-surface-primary bg-content-muted"></div>
                <div className="size-16 rounded-full border-4 border-surface-primary bg-accent flex items-center justify-center text-accent-contrast font-bold">
                  +5
                </div>
              </div>
            </section>
          )}
        </div>
      </main>
      <TrailsTour />
      </div>
  );
};

export default TrailsPage;
